import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  TenantProvider,
  type TenantMembership,
} from './shared/context/TenantContext';
import { useAuth, type BusinessMembership } from './shared/hooks/useAuth';
import { AdminLayout } from './layouts/AdminLayout';
import { BusinessLayout } from './layouts/BusinessLayout';
import { CalendarPage } from './pages/CalendarPage';
import { CashierPage } from './pages/CashierPage';
import { ConnectPage } from './pages/connect/ConnectPage';
import { DayClosePage } from './pages/DayClosePage';
import { InvoicesPage } from './pages/InvoicesPage';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage';
import { CustomersListPage } from './pages/customers/CustomersListPage';
import { DashboardPage } from './pages/DashboardPage';
import { GroomingPage } from './pages/grooming/GroomingPage';
import { BoardingPage } from './pages/boarding/BoardingPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { TimelinePage } from './pages/TimelinePage';
import { MigrationDetailPage } from './pages/admin/MigrationDetailPage';
import { MigrationsListPage } from './pages/admin/MigrationsListPage';
import { PoliciesListPage } from './pages/admin/PoliciesListPage';
import { PolicyDetailPage } from './pages/admin/PolicyDetailPage';
import { PolicySimulatorPage } from './pages/admin/PolicySimulatorPage';
import { ProfileDetailPage } from './pages/admin/ProfileDetailPage';
import { ProfilesListPage } from './pages/admin/ProfilesListPage';
import { SandboxPage } from './pages/admin/SandboxPage';
import { RemindersPage } from './pages/reminders/RemindersPage';
import { ReconciliationPage } from './pages/reconciliation/ReconciliationPage';
import { RevenueLeakPage } from './pages/revenue/RevenueLeakPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { ServicesPage } from './pages/services/ServicesPage';
import { StaffPage } from './pages/staff/StaffPage';
import { InventoryPage } from './pages/InventoryPage';
import { ReportsPage } from './pages/ReportsPage';
import { ProtectedRoute } from './components/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Adapter — projects useAuth's `BusinessMembership` shape onto the
 * legacy `TenantMembership` contract consumed by `TenantProvider` and
 * `TenantSwitcher`. The mapping is intentionally lossy on `role` so
 * the existing UI chips keep their fixed enum without forcing every
 * BE role into a new translation key.
 */
function toTenantMembership(m: BusinessMembership): TenantMembership {
  return {
    organizationId: m.organizationId ?? '',
    organizationName: m.organizationName ?? m.legalEntityName,
    legalEntityId: m.legalEntityId,
    legalEntityName: m.legalEntityName,
    branchId: m.branchId,
    branchName: m.branchName,
    businessProfileKey:
      m.verticalCapabilities[0]?.subVerticalKey ?? 'business.generic',
    verticalCapabilities: m.verticalCapabilities.map((vc) => vc.subVerticalKey),
    role: mapBusinessRoleToUi(m.role),
  };
}

function mapBusinessRoleToUi(
  role: BusinessMembership['role'],
): TenantMembership['role'] {
  switch (role) {
    case 'owner':
      return 'owner';
    case 'manager':
    case 'platform_admin':
      return 'manager';
    case 'reception':
      return 'receptionist';
    case 'cashier':
    case 'sales_associate':
      return 'finance';
    default:
      return 'staff';
  }
}

/**
 * AppShell — bridges the zustand auth store to the legacy TenantContext
 * surface. Memberships are read live from useAuth so a successful
 * login or `setActiveBranch` call propagates to TenantSwitcher without
 * a manual reload.
 *
 * The shell also owns the React Query cache invalidation on branch
 * switch (a swap is a tenant boundary; cached data is always stale).
 */
function AppShell(): JSX.Element {
  const memberships = useAuth((s) => s.memberships);
  const setActiveBranch = useAuth((s) => s.setActiveBranch);
  const qc = useQueryClient();

  const tenantMemberships = useMemo<TenantMembership[]>(
    () => memberships.map(toTenantMembership),
    [memberships],
  );

  const handleSwitch = useMemo(
    () =>
      async (branchId: string): Promise<TenantMembership[]> => {
        setActiveBranch(branchId);
        await qc.invalidateQueries();
        return tenantMemberships;
      },
    [setActiveBranch, qc, tenantMemberships],
  );

  return (
    <TenantProvider
      initialMemberships={tenantMemberships}
      onSwitch={handleSwitch}
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        <Route
          element={
            <ProtectedRoute>
              <BusinessLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/today" element={<TimelinePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/grooming" element={<GroomingPage />} />
          <Route path="/boarding" element={<BoardingPage />} />
          <Route path="/customers" element={<CustomersListPage />} />
          <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
          <Route path="/cashier" element={<CashierPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/day-close" element={<DayClosePage />} />
          <Route path="/reminders" element={<RemindersPage />} />
          <Route path="/revenue/leak" element={<RevenueLeakPage />} />
          <Route path="/reconciliation" element={<ReconciliationPage />} />
          <Route path="/connect" element={<ConnectPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/:tab" element={<SettingsPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/business-profiles" element={<ProfilesListPage />} />
          <Route
            path="/admin/business-profiles/:subVerticalKey"
            element={<ProfileDetailPage />}
          />
          <Route path="/admin/migrations" element={<MigrationsListPage />} />
          <Route path="/admin/migrations/:id" element={<MigrationDetailPage />} />
          <Route path="/admin/policies" element={<PoliciesListPage />} />
          <Route
            path="/admin/policies/:policyId/simulate"
            element={<PolicySimulatorPage />}
          />
          <Route path="/admin/policies/:policyId" element={<PolicyDetailPage />} />
          <Route path="/admin/sandbox" element={<SandboxPage />} />
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          className:
            'bg-biz-surface-elevated text-biz-foreground border border-biz-border shadow-biz-lg',
        }}
      />
    </TenantProvider>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AppShell />
    </QueryClientProvider>
  );
}
